import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./lib/AuthContext";
import { DataProvider } from "./lib/DataContext";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import VerifyEmail from "./pages/VerifyEmail";
import AcceptInvitation from "./pages/AcceptInvitation";
import AccountSettings from "./pages/AccountSettings";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ChangePassword from "./pages/ChangePassword";
import Dashboard from "./pages/Dashboard";
import MyWork from "./pages/MyWork";
import Tasks from "./pages/Tasks";
import Projects from "./pages/Projects";
import MyFavourites from "./pages/MyFavourites";
import Workspaces from "./pages/Workspaces";
import WorkspaceHome from "./pages/WorkspaceHome";
import Departments from "./pages/Departments";
import Dependencies from "./pages/Dependencies";
import FollowUps from "./pages/FollowUps";
import Approvals from "./pages/Approvals";
import Meetings from "./pages/Meetings";
import CommandCenter from "./pages/CommandCenter";
import Escalations from "./pages/Escalations";
import Performance from "./pages/Performance";
import Registers from "./pages/Registers";
import Calendar from "./pages/Calendar";
import Timeline from "./pages/Timeline";
import Templates from "./pages/Templates";
import Reports from "./pages/Reports";
import Notifications from "./pages/Notifications";
import Admin from "./pages/Admin";
import Intelligence from "./pages/Intelligence";
import Assistant from "./pages/Assistant";
import Automations from "./pages/Automations";

function Protected({ children }) {
  const { user, mustChange } = useAuth();
  if (user === null) return <div className="h-screen flex items-center justify-center text-slate-400">Loading…</div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (mustChange) return <ChangePassword forced />;
  return (
    <DataProvider>
      <Layout>{children}</Layout>
    </DataProvider>
  );
}

function Shell() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/dashboard" /> : <Login />} />
      <Route path="/signup" element={user ? <Navigate to="/dashboard" /> : <Signup />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/accept-invitation" element={user ? <Navigate to="/dashboard" /> : <AcceptInvitation />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/my-work" element={<Protected><MyWork /></Protected>} />
      <Route path="/tasks" element={<Protected><Tasks /></Protected>} />
      <Route path="/projects" element={<Protected><Projects /></Protected>} />
      <Route path="/favourites" element={<Protected><MyFavourites /></Protected>} />
      <Route path="/workspaces" element={<Protected><Workspaces /></Protected>} />
      <Route path="/workspaces/:wsId" element={<Protected><WorkspaceHome /></Protected>} />
      <Route path="/departments" element={<Protected><Departments /></Protected>} />
      <Route path="/dependencies" element={<Protected><Dependencies /></Protected>} />
      <Route path="/follow-ups" element={<Protected><FollowUps /></Protected>} />
      <Route path="/approvals" element={<Protected><Approvals /></Protected>} />
      <Route path="/meetings" element={<Protected><Meetings /></Protected>} />
      <Route path="/command-center" element={<Protected><CommandCenter /></Protected>} />
      <Route path="/escalations" element={<Protected><Escalations /></Protected>} />
      <Route path="/performance" element={<Protected><Performance /></Protected>} />
      <Route path="/registers" element={<Protected><Registers /></Protected>} />
      <Route path="/calendar" element={<Protected><Calendar /></Protected>} />
      <Route path="/timeline" element={<Protected><Timeline /></Protected>} />
      <Route path="/templates" element={<Protected><Templates /></Protected>} />
      <Route path="/reports" element={<Protected><Reports /></Protected>} />
      <Route path="/notifications" element={<Protected><Notifications /></Protected>} />
      <Route path="/intelligence" element={<Protected><Intelligence /></Protected>} />
      <Route path="/assistant" element={<Protected><Assistant /></Protected>} />
      <Route path="/automations" element={<Protected><Automations /></Protected>} />
      <Route path="/account-settings" element={<Protected><AccountSettings /></Protected>} />
      <Route path="/admin" element={<Protected><Admin /></Protected>} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <Shell />
      </AuthProvider>
    </BrowserRouter>
  );
}
